import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { BoutonLien, GRAND_BOUTON, IconeCamera, TitreEcran } from "./commun";
import type { Attente, Jeu } from "./jeu";
import { messageDeRefus } from "./textes";

type AttenteDe<T extends Attente["type"]> = Extract<Attente, { type: T }>;

/** La partie que le lecteur perdrait en recommencant : sa page, et de quoi y revenir. */
export interface PartieEnCours {
  page: number;
  code: string | null;
}

/** Recommencer avec un choix de la page 1 efface la partie : on le dit avant, code compris. */
function AvantDeRecommencer({ enCours }: { enCours: PartieEnCours }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-destructive/40 p-3">
      <p>
        Ce choix commence une nouvelle partie, et efface de ce téléphone votre partie actuelle (page{" "}
        {enCours.page}) et votre carnet.
      </p>
      {enCours.code ? (
        <p>
          Pour pouvoir y revenir, notez d'abord son code de sauvegarde :{" "}
          <span className="font-mono font-semibold tracking-wider">{enCours.code}</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Le choix scanne, a confirmer : deux QR codes empiles sur une page se lisent facilement l'un
 * pour l'autre, et un choix applique ne se defait pas (on ne triche pas en revenant en arriere).
 * Un choix de la page 1 en pleine partie se confirme aussi : il efface la partie en cours.
 */
export function Confirmation({
  attente,
  enCours,
  jeu,
}: {
  attente: AttenteDe<"confirmer">;
  enCours: PartieEnCours | null;
  jeu: Jeu;
}) {
  const perdue = attente.recommencer ? enCours : null;
  return (
    <section aria-labelledby="titre-confirmation" className="flex flex-col gap-6">
      <p className="text-muted-foreground">Page {attente.page}, vous avez scanné ce choix :</p>
      <TitreEcran id="titre-confirmation" className="font-serif text-2xl font-semibold">
        « {attente.libelle} »
      </TitreEcran>
      {perdue ? <AvantDeRecommencer enCours={perdue} /> : null}
      <div className="flex flex-col gap-3">
        <Button className={GRAND_BOUTON} onClick={() => jeu.confirmer()}>
          {perdue ? "Recommencer avec ce choix" : "C'est mon choix"}
        </Button>
        <Button variant="outline" className={GRAND_BOUTON} onClick={() => jeu.fermer()}>
          {perdue ? "Annuler" : "Ce n'est pas le bon"}
        </Button>
      </div>
      {perdue ? null : (
        <p className="text-sm text-muted-foreground">
          Pas le bon choix ? Revenez à la page et visez son QR code, un seul à la fois.
        </p>
      )}
    </section>
  );
}

/** Renoncer ne se rattrape pas : le lecteur le confirme, et la question prend le focus. */
function Renoncer({ onOui, onNon }: { onOui: () => void; onNon: () => void }) {
  const question = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    question.current?.focus();
  }, []);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <p ref={question} tabIndex={-1} className="outline-none">
        Renoncer compte comme une mauvaise réponse. Vous renoncez vraiment ?
      </p>
      <Button className={GRAND_BOUTON} onClick={onOui}>
        Oui, je renonce
      </Button>
      <Button variant="outline" className={GRAND_BOUTON} onClick={onNon}>
        Non, je cherche encore
      </Button>
    </div>
  );
}

/** Une enigme : la question, la reponse tapee. Renoncer vaut une mauvaise reponse. */
export function Enigme({
  attente,
  enCours,
  jeu,
}: {
  attente: AttenteDe<"enigme">;
  enCours: PartieEnCours | null;
  jeu: Jeu;
}) {
  const [reponse, setReponse] = useState("");
  const [renonce, setRenonce] = useState(false);
  const perdue = attente.recommencer ? enCours : null;
  return (
    <section aria-labelledby="titre-enigme" className="flex flex-col gap-5">
      <p className="text-muted-foreground">Énigme, page {attente.page}</p>
      <TitreEcran id="titre-enigme" className="font-serif text-2xl font-semibold">
        {attente.libelle}
      </TitreEcran>
      {perdue ? <AvantDeRecommencer enCours={perdue} /> : null}
      <form
        className="flex flex-col gap-3"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (reponse.trim() !== "") jeu.repondre(reponse);
        }}
      >
        <label htmlFor="reponse" className="text-lg">
          {attente.question || "Votre réponse ?"}
        </label>
        <input
          id="reponse"
          value={reponse}
          onChange={(evenement) => setReponse(evenement.target.value)}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          className="h-14 rounded-md border border-input bg-background px-4 text-xl"
        />
        <Button type="submit" className={GRAND_BOUTON} disabled={reponse.trim() === ""}>
          Valider ma réponse
        </Button>
      </form>
      {renonce ? (
        <Renoncer onOui={() => jeu.renoncer()} onNon={() => setRenonce(false)} />
      ) : (
        <div className="flex flex-col gap-2">
          <Button variant="outline" className={GRAND_BOUTON} onClick={() => setRenonce(true)}>
            Je renonce
          </Button>
          <p className="text-sm text-muted-foreground">
            Vous n'avez droit qu'à une réponse par énigme. Renoncer compte comme une mauvaise
            réponse, comme dans le livre.
          </p>
        </div>
      )}
      <div>
        <BoutonLien onClick={() => jeu.fermer()}>
          {perdue ? "Annuler" : `Revenir à la page ${attente.page} sans répondre`}
        </BoutonLien>
      </div>
    </section>
  );
}

/** Un code qui ne passe pas : ce qui se passe, et quoi faire. */
export function Refus({
  attente,
  sansPartie,
  jeu,
  onScanner,
  onTaper,
  onRecommencer,
}: {
  attente: AttenteDe<"refus">;
  sansPartie: boolean;
  jeu: Jeu;
  onScanner: () => void;
  onTaper: () => void;
  onRecommencer: () => void;
}) {
  const message = messageDeRefus(attente.raison, attente.page, sansPartie);
  const fin = attente.raison === "partie-finie" || attente.raison === "etat-perime";
  // Un choix de la page 1 : le lecteur a peut-etre repris le livre au debut. L'ecran suivant
  // fait confirmer, puisque cela efface la partie en cours.
  const avecCeChoix = attente.recommencer ? (
    <Button
      variant={fin ? "default" : "outline"}
      className={GRAND_BOUTON}
      onClick={() => jeu.recommencerAvec(attente.code, attente.via)}
    >
      Recommencer une partie avec ce choix
    </Button>
  ) : null;
  return (
    <section aria-labelledby="titre-refus" className="flex flex-col gap-5">
      <TitreEcran id="titre-refus" className="text-2xl font-semibold">
        {message.titre}
      </TitreEcran>
      <p>{message.texte}</p>
      <div className="flex flex-col gap-3">
        {fin ? (
          (avecCeChoix ?? (
            <Button className={GRAND_BOUTON} onClick={onRecommencer}>
              Recommencer une partie
            </Button>
          ))
        ) : (
          <>
            <Button className={GRAND_BOUTON} onClick={onScanner}>
              <IconeCamera />
              Scanner un autre choix
            </Button>
            <Button variant="outline" className={GRAND_BOUTON} onClick={onTaper}>
              Taper un code
            </Button>
            {avecCeChoix}
          </>
        )}
      </div>
      <div>
        <BoutonLien onClick={() => jeu.fermer()}>
          {sansPartie ? "Retour" : `Revenir à la page ${attente.page}`}
        </BoutonLien>
      </div>
    </section>
  );
}

/** Le meme choix scanne deux fois : rien a faire, sauf lire la page. */
export function DejaFait({ attente, jeu }: { attente: AttenteDe<"deja-fait">; jeu: Jeu }) {
  return (
    <section aria-labelledby="titre-deja-fait" className="flex flex-col gap-5">
      <TitreEcran id="titre-deja-fait" className="text-2xl font-semibold">
        Ce choix est déjà fait
      </TitreEcran>
      <p>
        Vous avez déjà choisi « {attente.libelle} ». Lisez la page {attente.page}.
      </p>
      <Button className={GRAND_BOUTON} onClick={() => jeu.fermer()}>
        Compris
      </Button>
    </section>
  );
}
